/**
 * Performance Budget Warning Component
 *
 * Displays warnings when performance metrics exceed defined budgets.
 * Can be used in development mode or shown to admins in production.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  performanceMonitor,
  BudgetViolation,
  WebVitalsMetric,
  DEFAULT_PERFORMANCE_BUDGETS,
} from '@/services/performanceMonitoring';

// =============================================================================
// TYPES
// =============================================================================

export interface PerformanceBudgetWarningProps {
  /**
   * Whether to show the warning panel
   * @default true in development
   */
  show?: boolean;

  /**
   * Position of the warning panel
   * @default 'bottom-right'
   */
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

  /**
   * Whether to show in development mode only
   * @default true
   */
  devOnly?: boolean;

  /**
   * Custom budget thresholds
   */
  budgets?: Partial<typeof DEFAULT_PERFORMANCE_BUDGETS>;

  /**
   * Callback when a violation occurs
   */
  onViolation?: (violation: BudgetViolation) => void;

  /**
   * Whether to auto-dismiss warnings after a delay
   * @default false
   */
  autoDismiss?: boolean;

  /**
   * Auto-dismiss delay in ms
   * @default 5000
   */
  autoDismissDelay?: number;

  /**
   * Maximum number of warnings to show
   * @default 5
   */
  maxWarnings?: number;
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  container: (position: string): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      position: 'fixed',
      zIndex: 99999,
      maxWidth: '400px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '12px',
    };

    switch (position) {
      case 'bottom-right':
        return { ...baseStyle, bottom: '20px', right: '20px' };
      case 'bottom-left':
        return { ...baseStyle, bottom: '20px', left: '20px' };
      case 'top-right':
        return { ...baseStyle, top: '20px', right: '20px' };
      case 'top-left':
        return { ...baseStyle, top: '20px', left: '20px' };
      default:
        return { ...baseStyle, bottom: '20px', right: '20px' };
    }
  },

  panel: {
    backgroundColor: '#1a1a2e',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    overflow: 'hidden',
  } as React.CSSProperties,

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: '#16213e',
    borderBottom: '1px solid #0f3460',
  } as React.CSSProperties,

  headerTitle: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  } as React.CSSProperties,

  badge: (type: 'warning' | 'error' | 'good'): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    padding: '0 6px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 600,
    backgroundColor:
      type === 'error' ? '#e94560' : type === 'warning' ? '#f39c12' : '#00cec9',
    color: '#fff',
  }),

  closeButton: {
    background: 'none',
    border: 'none',
    color: '#888',
    cursor: 'pointer',
    fontSize: '18px',
    lineHeight: 1,
    padding: '4px',
  } as React.CSSProperties,

  content: {
    maxHeight: '300px',
    overflowY: 'auto' as const,
    padding: '8px',
  } as React.CSSProperties,

  warningItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '10px 12px',
    backgroundColor: 'rgba(233, 69, 96, 0.1)',
    borderRadius: '6px',
    marginBottom: '6px',
    borderLeft: '3px solid #e94560',
  } as React.CSSProperties,

  warningIcon: {
    color: '#e94560',
    fontSize: '16px',
    flexShrink: 0,
  } as React.CSSProperties,

  warningContent: {
    flex: 1,
    minWidth: 0,
  } as React.CSSProperties,

  warningMetric: {
    color: '#fff',
    fontWeight: 600,
    marginBottom: '4px',
  } as React.CSSProperties,

  warningDetails: {
    color: '#aaa',
    fontSize: '11px',
    lineHeight: 1.4,
  } as React.CSSProperties,

  webVitals: {
    padding: '12px 16px',
    borderTop: '1px solid #0f3460',
  } as React.CSSProperties,

  webVitalsTitle: {
    color: '#888',
    fontSize: '11px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '10px',
  } as React.CSSProperties,

  webVitalItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
  } as React.CSSProperties,

  webVitalName: {
    color: '#ddd',
    fontSize: '12px',
  } as React.CSSProperties,

  webVitalValue: (rating: string): React.CSSProperties => ({
    fontWeight: 600,
    fontSize: '12px',
    color:
      rating === 'good'
        ? '#00cec9'
        : rating === 'needs-improvement'
        ? '#f39c12'
        : '#e94560',
  }),

  emptyState: {
    padding: '20px',
    textAlign: 'center' as const,
    color: '#666',
  } as React.CSSProperties,

  toggleButton: {
    position: 'fixed' as const,
    bottom: '20px',
    right: '20px',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#1a1a2e',
    border: '2px solid #0f3460',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    zIndex: 99998,
  } as React.CSSProperties,
};

// =============================================================================
// COMPONENT
// =============================================================================

export const PerformanceBudgetWarning: React.FC<PerformanceBudgetWarningProps> = ({
  show = true,
  position = 'bottom-right',
  devOnly = true,
  budgets,
  onViolation,
  autoDismiss = false,
  autoDismissDelay = 5000,
  maxWarnings = 5,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [violations, setViolations] = useState<BudgetViolation[]>([]);
  const [webVitals, setWebVitals] = useState<WebVitalsMetric[]>([]);

  // Check if should show based on environment
  const shouldShow = show && (!devOnly || process.env.NODE_ENV === 'development');

  // Update budgets if provided
  useEffect(() => {
    if (budgets) {
      performanceMonitor.setBudgets(budgets);
    }
  }, [budgets]);

  // Poll for violations and web vitals
  useEffect(() => {
    if (!shouldShow) return;

    const updateMetrics = () => {
      const currentViolations = performanceMonitor.getBudgetViolations();
      const currentWebVitals = performanceMonitor.getWebVitals();

      setViolations(currentViolations.slice(-maxWarnings));
      setWebVitals(currentWebVitals);

      // Check for new violations
      if (currentViolations.length > violations.length) {
        const newViolation = currentViolations[currentViolations.length - 1];
        if (onViolation) {
          onViolation(newViolation);
        }
        setIsVisible(true);

        // Auto-dismiss if enabled
        if (autoDismiss) {
          setTimeout(() => {
            setIsVisible(false);
          }, autoDismissDelay);
        }
      }
    };

    updateMetrics();
    const interval = setInterval(updateMetrics, 2000);

    return () => clearInterval(interval);
  }, [
    shouldShow,
    violations.length,
    onViolation,
    autoDismiss,
    autoDismissDelay,
    maxWarnings,
  ]);

  // Format metric value
  const formatValue = useCallback(
    (metric: string, value: number): string => {
      if (metric === 'CLS') {
        return value.toFixed(3);
      }
      return `${value.toFixed(0)}ms`;
    },
    []
  );

  // Format timestamp
  const formatTime = useCallback((timestamp: number): string => {
    return new Date(timestamp).toLocaleTimeString();
  }, []);

  if (!shouldShow) return null;

  // Show toggle button when collapsed
  if (!isVisible) {
    const hasIssues =
      violations.length > 0 ||
      webVitals.some((v) => v.rating !== 'good');

    if (!hasIssues) return null;

    return (
      <button
        style={styles.toggleButton}
        onClick={() => setIsVisible(true)}
        title="Show Performance Monitor"
        aria-label="Show performance monitor"
      >
        <span role="img" aria-hidden="true">
          {violations.length > 0 ? '\u26A0' : '\u2139'}
        </span>
      </button>
    );
  }

  return (
    <div style={styles.container(position)} role="status" aria-live="polite">
      <div style={styles.panel}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerTitle}>
            <span role="img" aria-hidden="true">\u26A1</span>
            Performance Monitor
            {violations.length > 0 && (
              <span style={styles.badge('warning')}>{violations.length}</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              style={styles.closeButton}
              onClick={() => setIsExpanded(!isExpanded)}
              title={isExpanded ? 'Collapse' : 'Expand'}
              aria-label={isExpanded ? 'Collapse panel' : 'Expand panel'}
            >
              {isExpanded ? '\u2212' : '+'}
            </button>
            <button
              style={styles.closeButton}
              onClick={() => setIsVisible(false)}
              title="Close"
              aria-label="Close panel"
            >
              \u00D7
            </button>
          </div>
        </div>

        {isExpanded && (
          <>
            {/* Violations */}
            {violations.length > 0 && (
              <div style={styles.content}>
                {violations.map((violation, index) => (
                  <div key={index} style={styles.warningItem}>
                    <span style={styles.warningIcon} role="img" aria-hidden="true">
                      \u26A0
                    </span>
                    <div style={styles.warningContent}>
                      <div style={styles.warningMetric}>
                        {violation.metric} budget exceeded
                      </div>
                      <div style={styles.warningDetails}>
                        Budget: {formatValue(violation.metric, violation.budget)}
                        {' | '}
                        Actual: {formatValue(violation.metric, violation.actual)}
                        <br />
                        {violation.context && (
                          <>
                            Context: {violation.context}
                            <br />
                          </>
                        )}
                        Time: {formatTime(violation.timestamp)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {violations.length === 0 && (
              <div style={styles.emptyState}>
                No budget violations detected
              </div>
            )}

            {/* Web Vitals Summary */}
            {webVitals.length > 0 && (
              <div style={styles.webVitals}>
                <div style={styles.webVitalsTitle}>Core Web Vitals</div>
                {webVitals.map((vital) => (
                  <div key={vital.name} style={styles.webVitalItem}>
                    <span style={styles.webVitalName}>{vital.name}</span>
                    <span style={styles.webVitalValue(vital.rating)}>
                      {vital.name === 'CLS'
                        ? vital.value.toFixed(3)
                        : `${vital.value.toFixed(0)}ms`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// PERFORMANCE INDICATOR COMPONENT
// =============================================================================

export interface PerformanceIndicatorProps {
  /**
   * Size of the indicator
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Whether to show in production
   * @default false
   */
  showInProduction?: boolean;
}

/**
 * Simple performance indicator that shows current status
 */
export const PerformanceIndicator: React.FC<PerformanceIndicatorProps> = ({
  size = 'medium',
  showInProduction = false,
}) => {
  const [status, setStatus] = useState<'good' | 'warning' | 'poor'>('good');

  const shouldShow =
    showInProduction || process.env.NODE_ENV === 'development';

  useEffect(() => {
    if (!shouldShow) return;

    const checkStatus = () => {
      const webVitals = performanceMonitor.getWebVitals();
      const violations = performanceMonitor.getBudgetViolations();

      if (violations.length > 3 || webVitals.some((v) => v.rating === 'poor')) {
        setStatus('poor');
      } else if (
        violations.length > 0 ||
        webVitals.some((v) => v.rating === 'needs-improvement')
      ) {
        setStatus('warning');
      } else {
        setStatus('good');
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 5000);

    return () => clearInterval(interval);
  }, [shouldShow]);

  if (!shouldShow) return null;

  const sizes = {
    small: { width: 8, height: 8 },
    medium: { width: 12, height: 12 },
    large: { width: 16, height: 16 },
  };

  const colors = {
    good: '#00cec9',
    warning: '#f39c12',
    poor: '#e94560',
  };

  return (
    <div
      style={{
        ...sizes[size],
        borderRadius: '50%',
        backgroundColor: colors[status],
        boxShadow: `0 0 ${size === 'small' ? 4 : size === 'medium' ? 6 : 8}px ${colors[status]}`,
      }}
      title={`Performance: ${status}`}
      role="status"
      aria-label={`Performance status: ${status}`}
    />
  );
};

export default PerformanceBudgetWarning;
